import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { QrCode, Loader2, Mail, ArrowLeft, Lock, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  // States for forgot password form (no token)
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  
  // States for reset password form (with token)
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    setVerifying(true);
    try {
      await axios.get(`${BACKEND_URL}/api/auth/verify-reset-token/${token}`);
      setTokenValid(true);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  // Handle forgot password request
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Veuillez entrer votre email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Email envoyé !');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Mot de passe réinitialisé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while verifying token
  if (token && verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <QrCode className="h-7 w-7 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-gray-900">ZOTMENU</span>
          </Link>
        </div>

        <Card className="border-0 shadow-xl">
          {/* Case 1: Token provided but invalid */}
          {token && !tokenValid && !verifying ? (
            <>
              <CardHeader className="text-center pb-4">
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <XCircle className="h-8 w-8 text-red-500" />
                </div>
                <CardTitle className="font-heading text-2xl">Lien invalide</CardTitle>
                <CardDescription>
                  Ce lien de réinitialisation est invalide ou a expiré.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-center text-gray-600 text-sm">
                  Les liens de réinitialisation expirent après 1 heure pour des raisons de sécurité.
                </p>
                <Button
                  onClick={() => { navigate('/reset-password'); window.location.reload(); }}
                  className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-semibold"
                >
                  Demander un nouveau lien
                </Button>
                <div className="text-center">
                  <Link to="/login" className="text-gray-600 hover:text-gray-900">
                    Retour à la connexion
                  </Link>
                </div>
              </CardContent>
            </>
          ) : token && tokenValid && success ? (
            /* Case 2: Password reset successful */
            <>
              <CardHeader className="text-center pb-4">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <CardTitle className="font-heading text-2xl">Mot de passe modifié !</CardTitle>
                <CardDescription>
                  Votre mot de passe a été réinitialisé avec succès.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-semibold"
                >
                  Se connecter
                </Button>
              </CardContent>
            </>
          ) : token && tokenValid ? (
            /* Case 3: Valid token - show reset form */
            <>
              <CardHeader className="text-center pb-4">
                <CardTitle className="font-heading text-2xl">Nouveau mot de passe</CardTitle>
                <CardDescription>
                  Choisissez un nouveau mot de passe sécurisé.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleResetSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="password">Nouveau mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 h-12"
                        data-testid="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">Minimum 6 caractères</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        id="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 h-12"
                        data-testid="confirm-password"
                      />
                    </div>
                  </div>

                  {password && confirmPassword && password !== confirmPassword && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <XCircle className="h-4 w-4" />
                      Les mots de passe ne correspondent pas
                    </p>
                  )}

                  <Button
                    type="submit"
                    disabled={loading || (password && confirmPassword && password !== confirmPassword)}
                    className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-semibold"
                    data-testid="reset-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Réinitialisation...
                      </>
                    ) : (
                      'Réinitialiser le mot de passe'
                    )}
                  </Button>
                </form>
              </CardContent>
            </>
          ) : sent ? (
            /* Case 4: Email sent confirmation */
            <>
              <CardHeader className="text-center pb-4">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <CardTitle className="font-heading text-2xl">Email envoyé !</CardTitle>
                <CardDescription>
                  Si un compte existe avec l'email <strong>{email}</strong>, vous recevrez un lien de réinitialisation.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-amber-50 rounded-xl text-sm text-amber-800">
                  <p className="font-medium mb-1">💡 Vérifiez vos spams</p>
                  <p>Si vous ne trouvez pas l'email, pensez à vérifier votre dossier spam.</p>
                </div>
                
                <Button
                  onClick={() => setSent(false)}
                  variant="outline"
                  className="w-full h-12 rounded-full"
                >
                  Renvoyer un email
                </Button>

                <div className="text-center">
                  <Link to="/login" className="text-orange-500 hover:text-orange-600 font-medium">
                    Retour à la connexion
                  </Link>
                </div>
              </CardContent>
            </>
          ) : (
            /* Case 5: Forgot password form (default) */
            <>
              <CardHeader className="text-center pb-4">
                <CardTitle className="font-heading text-2xl">Mot de passe oublié ?</CardTitle>
                <CardDescription>
                  Entrez votre email et nous vous enverrons un lien de réinitialisation.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleForgotSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="votre@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-12"
                        data-testid="forgot-email"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-semibold"
                    data-testid="forgot-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      'Envoyer le lien'
                    )}
                  </Button>

                  <div className="text-center">
                    <Link to="/login" className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-2">
                      <ArrowLeft className="h-4 w-4" />
                      Retour à la connexion
                    </Link>
                  </div>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
